import type { RangeDay } from '../lib/api';
import { CalendarDayCell, CalendarDayPlaceholder } from './CalendarDayCell';

// Block texture — every month is the same sheet of white paper; the block is
// told apart by a barely-there ink hatch behind the grid, mirrored for C. It
// has to be ink at low alpha: the old white hatching is invisible on white.
// (A border can't carry this — `.surface-raised` sets `border` unlayered, so
// any border utility on the same element is dead.)
const blockTint: Record<'A' | 'B' | 'C', string> = {
  A: '',
  B: 'bg-[repeating-linear-gradient(45deg,_transparent_0,_transparent_8px,_rgba(11,11,15,0.035)_8px,_rgba(11,11,15,0.035)_16px)]',
  C: 'bg-[repeating-linear-gradient(-45deg,_transparent_0,_transparent_8px,_rgba(11,11,15,0.035)_8px,_rgba(11,11,15,0.035)_16px)]',
};

// CalendarMonth renders one calendar-month section with its eyebrow header,
// the Mon..Sun weekday rule, and a 7-column grid of day cells.
//
// `days` is the subset of the parent's full RangeDay list that falls within
// this month. The component computes leading placeholders so the first row
// aligns with Monday-start columns.
export function CalendarMonth({
  monthLabel,
  block,
  weekRange,
  days,
  todayISO,
  betStartDate,
  completedDates,
  monthRef,
}: {
  monthLabel: string;
  block: 'A' | 'B' | 'C';
  weekRange?: string;
  days: RangeDay[];
  todayISO: string;
  betStartDate?: string | null;
  completedDates?: Set<string>;
  monthRef?: (el: HTMLElement | null) => void;
}) {
  if (days.length === 0) return null;

  // Leading offset: how many empty slots before the first cell so it lines up
  // with its weekday column. Monday=0, Sunday=6.
  const firstWeekdayMonStart = (days[0].weekday + 6) % 7;
  const tintClass = blockTint[block] ?? '';

  const eyebrow = [
    monthLabel,
    `Block ${block}`,
    weekRange,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section
      ref={monthRef}
      data-month={monthLabel}
      className={`surface-raised p-4 space-y-3 ${tintClass}`}
    >
      <header className="flex items-baseline justify-between gap-2">
        <p className="label-eyebrow">{eyebrow}</p>
      </header>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span
            key={i}
            className="font-mono text-[9px] uppercase tracking-[0.18em] text-fg-faint"
          >
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstWeekdayMonStart }).map((_, i) => (
          <CalendarDayPlaceholder key={`pad-${i}`} />
        ))}
        {days.map((d) => (
          <CalendarDayCell
            key={d.date}
            day={d}
            isToday={d.date === todayISO}
            inFuture={d.date > todayISO}
            isPreBet={betStartDate ? d.date < betStartDate : false}
            isCompleted={completedDates?.has(d.date)}
          />
        ))}
      </div>
    </section>
  );
}

// groupByMonth chunks a chronologically-ordered RangeDay array into
// per-month buckets. Returns objects with the YYYY-MM key, a human label,
// the dominant block (whatever the 1st of the month resolves to), and the
// week range (first..last week_number > 0 within the month).
export function groupByMonth(days: RangeDay[]): {
  key: string;
  label: string;
  block: 'A' | 'B' | 'C';
  weekRange?: string;
  days: RangeDay[];
}[] {
  const buckets = new Map<string, RangeDay[]>();
  for (const d of days) {
    const k = d.date.slice(0, 7);
    const arr = buckets.get(k) ?? [];
    arr.push(d);
    buckets.set(k, arr);
  }
  return Array.from(buckets.entries()).map(([key, arr]) => {
    const first = arr[0];
    const dt = new Date(`${key}-01T00:00:00`);
    const label = dt.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
    const weeks = arr.map((d) => d.week_number).filter((w) => w > 0);
    const weekRange = weeks.length
      ? weeks[0] === weeks[weeks.length - 1]
        ? `Wk ${weeks[0]}`
        : `Wk ${weeks[0]}–${weeks[weeks.length - 1]}`
      : undefined;
    return {
      key,
      label: label.toUpperCase(),
      block: first.block,
      weekRange,
      days: arr,
    };
  });
}
